// Video embed utilities for React-Quill

/**
 * Extract video ID from various YouTube URL formats
 * @param {string} url - YouTube URL
 * @returns {string|null} - Video ID or null if invalid
 */
export const extractYouTubeId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

/**
 * Extract video ID from Vimeo URL
 * @param {string} url - Vimeo URL
 * @returns {string|null} - Video ID or null if invalid
 */
export const extractVimeoId = (url) => {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
};

/**
 * Create YouTube embed HTML
 * @param {string} videoId - YouTube video ID
 * @param {number} width - Video width (default: 560)
 * @param {number} height - Video height (default: 315)
 * @returns {string} - Embed HTML
 */
export const createYouTubeEmbed = (videoId, width = 560, height = 315) => {
  return `<iframe width="${width}" height="${height}" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
};

/**
 * Create Vimeo embed HTML
 * @param {string} videoId - Vimeo video ID
 * @param {number} width - Video width (default: 560)
 * @param {number} height - Video height (default: 315)
 * @returns {string} - Embed HTML
 */
export const createVimeoEmbed = (videoId, width = 560, height = 315) => {
  return `<iframe src="https://player.vimeo.com/video/${videoId}" width="${width}" height="${height}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
};

/**
 * Custom video handler for Quill toolbar
 * Prompts user for video URL and embeds it
 */
export const videoHandler = function() {
  const url = prompt('Enter YouTube or Vimeo URL:');
  if (!url) return;

  const quill = this.quill;
  const range = quill.getSelection();
  
  // Try YouTube first
  const youtubeId = extractYouTubeId(url);
  if (youtubeId) {
    const embedHtml = createYouTubeEmbed(youtubeId);
    quill.clipboard.dangerouslyPasteHTML(range.index, embedHtml);
    return;
  }
  
  // Try Vimeo
  const vimeoId = extractVimeoId(url);
  if (vimeoId) {
    const embedHtml = createVimeoEmbed(vimeoId);
    quill.clipboard.dangerouslyPasteHTML(range.index, embedHtml);
    return;
  }
  
  // If neither, show error
  alert('Invalid video URL. Please use a YouTube or Vimeo link.');
};

/**
 * Auto-detect and convert video URLs in pasted content
 * @param {string} html - HTML content to process
 * @returns {string} - Processed HTML with video embeds
 */
export const processVideoUrls = (html) => {
  // Replace YouTube URLs with embeds
  html = html.replace(
    /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s<>"]+)/g,
    (match, videoId) => createYouTubeEmbed(videoId)
  );
  
  // Replace Vimeo URLs with embeds
  html = html.replace(
    /https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/g,
    (match, videoId) => createVimeoEmbed(videoId)
  );
  
  return html;
};